import { useCallback, useMemo } from 'react'

import { CreatorPermission, DetailedPermission } from '@api'
import type { ColumnDef } from '@tanstack/react-table'
import { useUnit } from 'effector-react'
import type { Control } from 'react-hook-form'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { Checkbox } from '@/components/ui/checkbox'
import { FormCheckbox } from '@/components/ui/form/form-checkbox'
import { DataTable } from '@/components/ui/table/data-table'
import { Tooltip } from '@/components/ui/tooltip'
import { $creators } from '@/models/creators.ts'
import { OfPermissionsCombobox } from '@/modules/creators/ui/OfPermissionsCombobox'

import type {
  PermissionBlock,
  PermissionBlockModifiedPermissions,
  TeamRolePermissionAssignment,
} from '../types'
import { DeselectButton } from './deselect-button'

// Type guard to check if a string is a valid CreatorPermission
const isValidCreatorPermission = (
  value: string,
): value is CreatorPermission => {
  return Object.values(CreatorPermission).includes(value as CreatorPermission)
}

interface Props<TFormValues> {
  allPermissions: DetailedPermission[]
  ofPermissions: DetailedPermission[]
  generalPermissions: DetailedPermission[]
  defaultSelectedPermissions?: PermissionBlock[]
  accessOnlyFansProfilePermission?: DetailedPermission
  onChangePermissions?: (permissions: TeamRolePermissionAssignment[]) => void
  isSuperCreatorEnabled?: boolean
  isEditEnabled?: boolean
  mode?: 'MEMBER' | 'ROLE'
  control: TFormValues extends PermissionsTableFormFields
    ? Control<TFormValues>
    : never
}

export interface PermissionsTableFormFields {
  permissionsTable: {
    permissions: PermissionBlock[]
  }
}

export const PermissionsTableField = <T extends PermissionsTableFormFields>({
  mode = 'ROLE',
  isSuperCreatorEnabled,
  allPermissions,
  generalPermissions,
  accessOnlyFansProfilePermission,
  ofPermissions,
  isEditEnabled = true,
  control,
}: Props<T>) => {
  const allCreators = useUnit($creators)

  const { setValue, getValues } = useFormContext<PermissionsTableFormFields>()

  const watch = useWatch({ control })

  const { fields } = useFieldArray({
    control,
    name: 'permissionsTable.permissions',
  })

  const handleSelectAllCreatorPermissions = useCallback(
    (permission: CreatorPermission, value: boolean) => {
      allCreators.forEach((_, index) => {
        setValue(
          `permissionsTable.permissions.${index + (isSuperCreatorEnabled ? 1 : 0)}.permissions.${permission}`,
          value,
        )
      })
    },
    [allCreators, isSuperCreatorEnabled, setValue],
  )

  const handleChangeOFPermission = useCallback(
    (index: number, permission: CreatorPermission, value: boolean) => {
      setValue(
        `permissionsTable.permissions.${index}.permissions.${permission}`,
        value,
        {
          shouldDirty: true,
        },
      )
    },
    [setValue],
  )

  const getPermissionColumn = useCallback(
    (permission: DetailedPermission): ColumnDef<PermissionBlock> => ({
      accessorKey: `permissionsTable.permissions.${permission.value}`,
      header: () => {
        const allArrayFields = getValues().permissionsTable.permissions || []
        const isAllSelected = allArrayFields
          ?.slice(isSuperCreatorEnabled ? 1 : 0)
          .every((val) => {
            const permissionValue = permission.value

            return (
              permissionValue &&
              isValidCreatorPermission(permissionValue) &&
              val.permissions?.[permissionValue] === true
            )
          })

        return (
          <div className="flex h-full flex-col items-center justify-center pb-2 text-center">
            <span className="pb-2">{permission.displayName}</span>
            <span className="mt-auto">
              <DeselectButton
                isSelected={isAllSelected}
                disabled={!isEditEnabled}
                onToggle={(toggleValue) =>
                  permission.value &&
                  isValidCreatorPermission(permission.value) &&
                  handleSelectAllCreatorPermissions(
                    permission.value,
                    toggleValue,
                  )
                }
              />
            </span>
          </div>
        )
      },
      id: permission.value,
      cell: ({ row }) => {
        const superCreatorPermissions =
          getValues().permissionsTable.permissions.find(
            (val) => val.type === 'SUPER_CREATOR',
          )
        const superCreatorPermissionsValue =
          permission.value && isValidCreatorPermission(permission.value)
            ? superCreatorPermissions?.permissions?.[permission.value]
            : false
        const isSuperCreatorPermission = row.original.type === 'SUPER_CREATOR'
        const selectedByRoles =
          permission.value && isValidCreatorPermission(permission.value)
            ? row.original.permissionsReasonList?.[permission.value]
            : undefined

        return isSuperCreatorPermission ? (
          <Tooltip
            className="flex w-full justify-center"
            content={
              (!isEditEnabled &&
                'You cannot edit permissions of a default role.') ||
              `Enabling this permission will enable ${permission.displayName} for all current and future creators.`
            }
          >
            <div className="flex justify-center">
              <FormCheckbox
                name={`permissionsTable.permissions.${row.index}.permissions.${permission.value}`}
                disabled={!isEditEnabled}
              />
            </div>
          </Tooltip>
        ) : (
          <Tooltip
            className="flex w-full justify-center"
            content={
              (!isEditEnabled &&
                'You cannot edit permissions of a default role') ||
              (selectedByRoles &&
                `This permission is inherited from these roles: ${selectedByRoles.join(', ')}`)
            }
          >
            <div className="flex justify-center">
              {selectedByRoles ||
              (!isSuperCreatorPermission && superCreatorPermissionsValue) ? (
                <Checkbox
                  checked
                  disabled
                />
              ) : (
                <FormCheckbox
                  name={`permissionsTable.permissions.${row.index}.permissions.${permission.value}`}
                  disabled={
                    (!isSuperCreatorPermission &&
                      superCreatorPermissionsValue) ||
                    !isEditEnabled
                  }
                />
              )}
            </div>
          </Tooltip>
        )
      },
    }),
    [
      getValues,
      handleSelectAllCreatorPermissions,
      isEditEnabled,
      isSuperCreatorEnabled,
    ],
  )

  const creatorNameColumn: ColumnDef<PermissionBlock> = useMemo(
    () => ({
      accessorKey: 'creatorName',
      header: 'Creator Name',
      cell: ({ row }) => {
        const creatorName = row.original.creatorName

        const hasSelectedFields = allPermissions.some((permission) => {
          const permissionValue = permission.value

          return (
            permissionValue &&
            isValidCreatorPermission(permissionValue) &&
            watch?.permissionsTable?.permissions?.[row.index].permissions?.[
              permissionValue
            ]
          )
        })
        const superCreatorPermissionsAllSelected =
          isSuperCreatorEnabled &&
          Object.values(
            watch.permissionsTable?.permissions?.find(
              (val) => val.type === 'SUPER_CREATOR',
            )?.permissions || {},
          ).every((val) => val === true) &&
          row.original.type !== 'SUPER_CREATOR'

        const selectAllFields = (select: boolean) => {
          for (const permission of allPermissions) {
            const permissionValue = permission.value

            if (permissionValue && isValidCreatorPermission(permissionValue)) {
              setValue(
                `permissionsTable.permissions.${row.index}.permissions.${permissionValue}` as const,
                select,
                { shouldDirty: true },
              )
            }
          }
        }

        return (
          <div className="sticky left-[0px] flex items-center justify-between gap-3">
            {row.original.type === 'SUPER_CREATOR' ? (
              <Tooltip
                className="text-left"
                content="Modifying permissions in this row will affect permissions for all current and future creators."
              >
                <span className="text-left font-bold">{creatorName}</span>
              </Tooltip>
            ) : (
              <p className="max-w-[100px] overflow-hidden text-ellipsis whitespace-nowrap">
                {creatorName}
              </p>
            )}
            <DeselectButton
              onToggle={selectAllFields}
              isSelected={hasSelectedFields}
              disabled={superCreatorPermissionsAllSelected || !isEditEnabled}
            />
          </div>
        )
      },
    }),
    [
      allPermissions,
      isEditEnabled,
      isSuperCreatorEnabled,
      setValue,
      watch.permissionsTable?.permissions,
    ],
  )

  const ofPermissionsColumn: ColumnDef<PermissionBlock> = useMemo(() => {
    return {
      header: 'OnlyFans Permissions',
      accessorKey: 'onlyfans_permissions',
      cell: ({ row }) => {
        const superCreatorPermissions =
          getValues().permissionsTable.permissions.find(
            (val) => val.type === 'SUPER_CREATOR',
          )
        const disabledPermissions = ofPermissions.reduce<
          Partial<Record<CreatorPermission, string>>
        >((acc, permission) => {
          const permissionValue = permission.value

          if (!permissionValue || !isValidCreatorPermission(permissionValue))
            return acc

          const superCreatorPermissionsValue =
            superCreatorPermissions?.permissions?.[permissionValue]
          const isSuperCreatorPermission = row.original.type === 'SUPER_CREATOR'
          const selectedByRoles =
            mode === 'MEMBER' &&
            row.original.permissionsReasonList?.[permissionValue]

          if (
            (!isSuperCreatorPermission && superCreatorPermissionsValue) ||
            !!selectedByRoles
          ) {
            acc[permissionValue] = selectedByRoles
              ? `This permission is inherited from these roles: ${selectedByRoles.join(', ')}`
              : "Selected in 'All Creators'"
          }

          return acc
        }, {})

        const creatorsPermissions =
          getValues().permissionsTable.permissions[row.index].permissions

        const selectedPermissionsByRole = ofPermissions
          .filter((permission) => {
            const permissionValue = permission.value

            return (
              permissionValue &&
              isValidCreatorPermission(permissionValue) &&
              row.original.permissionsReasonList?.[permissionValue]
            )
          })
          .reduce<Partial<PermissionBlockModifiedPermissions>>(
            (acc, permission) => {
              const permissionValue = permission.value

              if (
                permissionValue &&
                isValidCreatorPermission(permissionValue)
              ) {
                acc[permissionValue] = true
              }

              return acc
            },
            {},
          )

        const selectedPermissions = {
          ...creatorsPermissions,
          ...selectedPermissionsByRole,
        }

        const hasAccessToOnlyFansProfile =
          getValues(
            `permissionsTable.permissions.${row.index}.permissions.ACCESS_ONLYFANS_PROFILE`,
          ) || !!row.original.permissionsReasonList?.ACCESS_ONLYFANS_PROFILE

        return (
          <OfPermissionsCombobox
            ofPermissions={ofPermissions}
            disabledPermissions={disabledPermissions}
            selectedPermissions={selectedPermissions}
            index={row.index}
            onChangeOfPermission={handleChangeOFPermission}
            disabled={!hasAccessToOnlyFansProfile}
          />
        )
      },
    }
  }, [getValues, handleChangeOFPermission, mode, ofPermissions])

  const generalPermissionColumns = useMemo(
    () =>
      generalPermissions
        .filter(
          (permission) =>
            permission.value !== CreatorPermission.FAN_NUMBER_SUPERVISOR,
        )
        .map(getPermissionColumn),
    [generalPermissions, getPermissionColumn],
  )

  const accessOnlyFansProfileColumn = useMemo(
    () =>
      accessOnlyFansProfilePermission
        ? getPermissionColumn(accessOnlyFansProfilePermission)
        : null,
    [accessOnlyFansProfilePermission, getPermissionColumn],
  )

  const columns: ColumnDef<PermissionBlock>[] = useMemo(
    () =>
      [
        creatorNameColumn,
        accessOnlyFansProfileColumn,
        ofPermissionsColumn,
        ...generalPermissionColumns,
      ].filter(Boolean) as ColumnDef<PermissionBlock>[],
    [
      accessOnlyFansProfileColumn,
      creatorNameColumn,
      generalPermissionColumns,
      ofPermissionsColumn,
    ],
  )

  return (
    <DataTable
      columns={columns}
      data={fields}
    />
  )
}
